import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../../store';
import { Status } from '../../../common/constants';
import {
  IDateRange,
  IAlertSeverityData,
  IAlertSourceData,
  IOrganizationAlertStatisticsItem,
  IGlobalAlertStatistics,
} from '../analytics.types';
import { getAlertsAnalyticsApi } from '../analyticsApi';
import { normalizeAlertsAnalyticsResponse } from '../analyticsUtils';

const GET_ALERTS_ANALYTICS = 'analytics/getAlertsAnalytics';


interface IAlertsAnalyticsState {
  status: string;
  error: string | null;
  totalAlertsCount: number;
  totalActiveAlerts: number;
  alertsBySeverity: IAlertSeverityData[];
  alertsBySource: IAlertSourceData[];
  orgStatistics: IOrganizationAlertStatisticsItem[];
  globalAlertStats: IGlobalAlertStatistics;
}

const initialState: IAlertsAnalyticsState = {
  status: Status.IDLE,
  error: null,
  totalAlertsCount: 0,
  totalActiveAlerts: 0,
  alertsBySeverity: [],
  alertsBySource: [],
  orgStatistics: [],
  globalAlertStats: {
    totalAlerts: 0,
    successCount: 0,
    failedCount: 0,
    avgSuccessRate: 0,
    failedRate: 0,
    sourceStats: [],
    severityStats: [],
  },
};

export const getAlertsAnalytics = createAsyncThunk(
  GET_ALERTS_ANALYTICS,
  async (dateRange: IDateRange) => {
    const alertsAnalyticsResponse = await getAlertsAnalyticsApi(dateRange);
    return normalizeAlertsAnalyticsResponse(alertsAnalyticsResponse);
  }
);

const alertsAnalyticsSlice = createSlice({
  name: 'alertsAnalytics',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAlertsAnalytics.pending, (state) => {
        state.status = Status.PENDING;
        state.error = null;
      })
      .addCase(getAlertsAnalytics.fulfilled, (state, action) => {
        state.status = Status.SUCCESS;
        state.error = null;
        state.totalAlertsCount = action.payload.totalAlertsCount;
        state.totalActiveAlerts = action.payload.totalActiveAlerts;
        state.alertsBySeverity = action.payload.alertsBySeverity;
        state.alertsBySource = action.payload.alertsBySource;
        state.orgStatistics = action.payload.orgStatistics;
        state.globalAlertStats = action.payload.globalAlertStats;
      })
      .addCase(getAlertsAnalytics.rejected, (state, action) => {
        state.status = Status.FAILED;
        state.error =
          action.error.message || 'Failed to fetch alerts analytics.';
      });
  },
});

export const selector = (state: RootState) => state.alertsAnalytics;

export default alertsAnalyticsSlice.reducer;